"use client";
import type { Queryable } from "@livestore/livestore";
import { useQuery } from "@livestore/react";

/**
 * Renders the value of a LiveQuery using a render function.
 * This function can use hooks like useMemo etc within
 * @example
 * ```tsx
 * <LiveValue query={isEnabled$}>
 *   {(value) => <div data-loc="phosphor-blog/content/_sources/autocomplete-demo/components/LiveValue.tsx:11:23" data-name="div">{value ? "Enabled" : "Disabled"}</div>}
 * </LiveValue>
 * ```
 * @param props.query - The LiveQuery to subscribe to.
 * @param props.children - The function to render the value.
 * @returns The rendered value.
 */
export function LiveValue<T>(props: {
  query: Queryable<T>;
  children: (value: T) => React.ReactNode;
}): React.ReactNode;
/**
 * @example
 * ```tsx
 * // Render as text
 * <LiveValue query={displayName$} />
 * ```
 * @param props.query - The LiveQuery to subscribe to.
 * @returns The rendered value.
 */
export function LiveValue(props: {
  query: Queryable<React.ReactNode | string | number | boolean | null | undefined>;
}): React.ReactNode;
export function LiveValue(props: {
  query: Queryable<any>;
  children?: (value: any) => React.ReactNode;
}) {
  const value = useQuery(props.query as any);
  return <>{typeof props.children === "function" ? props.children(value) : (value as any)}</>;
}
